/**
 * Asset resolution utility
 * Handles graceful fallbacks for missing assets
 */

export type AssetType = 'image' | 'document' | 'icon';

/**
 * Resolves asset paths and provides fallbacks
 * @param path - The path to the asset
 * @param type - The type of asset (defaults to 'image')
 * @returns The resolved URL to the asset
 */
export const resolveAsset = (path: string, type: AssetType = 'image'): string => {
  try {
    // For imported assets in src directory
    if (path.startsWith('/src/') || path.startsWith('src/')) {
      try {
        // Try to import using URL constructor for module imports
        return new URL(`../../${path.replace(/^\/src\/|^src\//, '')}`, import.meta.url).href;
      } catch (error) {
        console.warn(`Asset import failed: ${path}`, error);
        return getFallbackAsset(type);
      }
    }
    
    // For assets in public directory
    if (path.startsWith('/')) {
      return path;
    }
    
    // Relative paths within src
    return new URL(`../../assets/${path}`, import.meta.url).href;
  } catch (error) {
    console.warn(`Asset not found: ${path}`, error);
    return getFallbackAsset(type);
  }
};

/**
 * Get a fallback asset based on type
 * @param type - The type of asset
 * @returns A URL to a fallback asset
 */
const getFallbackAsset = (type: AssetType): string => {
  switch (type) {
    case 'image':
      // Return a transparent 1x1 pixel data URL
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    case 'icon':
      // Return a simple icon data URL (a small circle)
      return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2Ij48Y2lyY2xlIGN4PSI4IiBjeT0iOCIgcj0iNCIgZmlsbD0iIzk5OSIvPjwvc3ZnPg==';
    case 'document':
      // Return an empty document URL
      return 'about:blank';
    default:
      return '';
  }
};

/**
 * Checks if an asset exists at the given path
 * @param path - The path to check
 * @returns Promise that resolves to a boolean indicating existence
 */
export const assetExists = async (path: string): Promise<boolean> => {
  try {
    const response = await fetch(path, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Gets team member initials for avatar fallbacks
 * @param name - Full name of the person
 * @returns Initials (first letter of first and last name)
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  
  // Split the name and take first letter of first two words
  const parts = name.split(' ').filter(Boolean);
  
  if (parts.length === 1) {
    // If single word, take first two letters
    const firstPart = parts[0];
    if (firstPart && firstPart.length >= 2) {
      return firstPart.substring(0, 2).toUpperCase();
    }
    return firstPart ? firstPart.toUpperCase() : '';
  }
  
  // Take first letter of first and last word
  const firstPart = parts[0] || '';
  const lastPart = parts.length > 1 ? parts[parts.length - 1] || '' : '';
  
  const firstChar = firstPart.length > 0 ? firstPart[0] : '';
  const lastChar = lastPart.length > 0 ? lastPart[0] : '';
  
  return `${firstChar}${lastChar}`.toUpperCase();
};